import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Heart, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { LogoMark, LogoWordmark } from "./Logo";
import { STOREFRONT_URL } from "../data/products";

const LINKS = [
  { label: "Finds", href: "#products" },
  { label: "Categories", href: "#categories" },
  { label: "Why Us", href: "#why" },
];

export const Navbar = ({ user, onLogout, onSignIn, favoritesCount, onOpenWishlist, onOpenAdmin }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8 py-4">
        <a
          data-testid="logo-home-link"
          href="#top"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3"
        >
          <LogoMark size={34} />
          <LogoWordmark />
        </a>
        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              className="text-sm text-slate-300 hover:text-[#FF9900] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-2.5" data-testid="nav-user-menu">
              <button
                data-testid="nav-wishlist-btn"
                onClick={onOpenWishlist}
                aria-label="Open wishlist"
                className="relative rounded-full border border-white/10 p-2 text-slate-300 transition-colors duration-200 hover:border-[#FF9900]/50 hover:text-[#FF9900]"
              >
                <Heart size={14} className={favoritesCount > 0 ? "fill-[#FF9900] text-[#FF9900]" : ""} />
                {favoritesCount > 0 && (
                  <span
                    data-testid="nav-wishlist-count"
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF9900] px-1 font-mono text-[9px] font-bold text-[#0B0C0E]"
                  >
                    {favoritesCount}
                  </span>
                )}
              </button>
              {user.is_admin && (
                <button
                  data-testid="nav-admin-btn"
                  onClick={onOpenAdmin}
                  aria-label="Open admin panel"
                  className="rounded-full border border-[#FF9900]/40 bg-[#FF9900]/10 p-2 text-[#FF9900] transition-colors duration-200 hover:bg-[#FF9900]/20"
                >
                  <ShieldCheck size={14} />
                </button>
              )}
              {user.picture ? (
                <img
                  data-testid="nav-user-avatar"
                  src={user.picture}
                  alt={user.name}
                  referrerPolicy="no-referrer"
                  className="h-8 w-8 rounded-full border border-[#FF9900]/40 object-cover"
                />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#FF9900]/40 bg-[#121418] text-[#FF9900]">
                  <UserRound size={15} />
                </span>
              )}
              <span className="hidden sm:block max-w-28 truncate text-xs font-semibold text-slate-200">
                {user.name}
              </span>
              <button
                data-testid="nav-logout-btn"
                onClick={onLogout}
                aria-label="Sign out"
                className="rounded-full border border-white/10 p-2 text-slate-400 transition-colors duration-200 hover:border-[#FF9900]/50 hover:text-[#FF9900]"
              >
                <LogOut size={13} />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-sign-in-btn"
              onClick={onSignIn}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-[#FF9900]/50 hover:text-[#FF9900] active:scale-95"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#EA4335" d="M12 5.04c1.7 0 3.22.59 4.42 1.73l3.29-3.29C17.72 1.7 15.06.5 12 .5 7.44.5 3.55 3.11 1.61 6.93l3.83 2.97C6.36 7.22 8.94 5.04 12 5.04z" />
                <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45c-.28 1.48-1.12 2.73-2.38 3.57l3.71 2.88c2.17-2 3.72-4.96 3.72-8.64z" />
                <path fill="#FBBC05" d="M5.44 14.1a7.2 7.2 0 0 1 0-4.2L1.61 6.93a11.5 11.5 0 0 0 0 10.14l3.83-2.97z" />
                <path fill="#34A853" d="M12 23.5c3.06 0 5.63-1.01 7.5-2.74l-3.71-2.88c-1 .68-2.3 1.08-3.79 1.08-3.06 0-5.64-2.18-6.56-5.06l-3.83 2.97C3.55 20.39 7.44 23.5 12 23.5z" />
              </svg>
              Sign in
            </button>
          )}
          <a
            data-testid="nav-shop-amazon-btn"
            href={STOREFRONT_URL}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FF9900] px-4 py-2 text-sm font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#FFB800] active:scale-95"
          >
            Shop Amazon
            <ExternalLink size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </nav>
    </motion.header>
  );
};
